/**
 * Tier 1 environment capture (§1.6) — collected silently, no consent required.
 *
 * Everything here is non-identifying diagnostic context about the browser. Financial
 * identifiers (wallet addresses, connected exchanges) are Tier 2 and live behind an
 * explicit opt-in — they are deliberately NOT gathered by this function.
 *
 * Identity (name/email/user id) is also absent on purpose: the server resolves it from
 * the session. Anything the client claims about who it is would be ignored.
 */
export interface EnvironmentContext {
  browser: string;
  browser_version: string;
  operating_system: string;
  device: string;
  screen_resolution: string;
  timezone: string;
  language: string;
  app_version: string;
  current_url: string;
}

const UNKNOWN = 'unknown';

function unknownEnvironment(appVersion: string): EnvironmentContext {
  return {
    browser: UNKNOWN,
    browser_version: UNKNOWN,
    operating_system: UNKNOWN,
    device: UNKNOWN,
    screen_resolution: UNKNOWN,
    timezone: UNKNOWN,
    language: UNKNOWN,
    app_version: appVersion,
    current_url: UNKNOWN
  };
}


export function captureEnvironment(appVersion: string): EnvironmentContext {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return unknownEnvironment(appVersion);
  }

  const ua = navigator.userAgent ?? '';
  const { name, version } = detectBrowser(ua);

  return {
    browser: name,
    browser_version: version,
    operating_system: detectOs(ua),
    device: detectDevice(ua),
    // `screen` is absent in some headless/jsdom setups even when `window` exists.
    screen_resolution: window.screen
      ? `${window.screen.width}x${window.screen.height}`
      : UNKNOWN,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? UNKNOWN,
    language: navigator.language ?? UNKNOWN,
    app_version: appVersion,
    current_url: window.location?.href ?? UNKNOWN
  };
}

/**
 * Order matters: Edge and Opera both include "Chrome" in their UA string, and Chrome
 * includes "Safari". Checking the most specific first avoids reporting every Edge user
 * as a Chrome user.
 */
function detectBrowser(ua: string): { name: string; version: string } {
  const checks: ReadonlyArray<readonly [string, RegExp]> = [
    ['Edge', /Edg\/([\d.]+)/],
    ['Opera', /OPR\/([\d.]+)/],
    ['Samsung Internet', /SamsungBrowser\/([\d.]+)/],
    ['Firefox', /Firefox\/([\d.]+)/],
    ['Chrome', /Chrome\/([\d.]+)/],
    ['Safari', /Version\/([\d.]+).*Safari/]
  ];

  for (const [name, re] of checks) {
    const m = ua.match(re);
    if (m) return { name, version: m[1] ?? 'unknown' };
  }
  return { name: 'Unknown', version: 'unknown' };
}

function detectOs(ua: string): string {
  if (/Windows NT 10/.test(ua)) return 'Windows 10/11';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Android/.test(ua)) return 'Android';
  // iPadOS reports as Macintosh, so check touch support to tell them apart.
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Mac OS X/.test(ua)) {
    return navigator.maxTouchPoints > 1 ? 'iPadOS' : 'macOS';
  }
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

function detectDevice(ua: string): string {
  if (/Mobile|iPhone|Android.*Mobile/.test(ua)) return 'Mobile';
  if (/iPad|Tablet|Android/.test(ua)) return 'Tablet';
  return 'Desktop';
}
